import React, { useState } from 'react';
import { Upload, FileText, CheckCircle2, AlertCircle, X } from 'lucide-react';
import { storage, db } from '../../../firebase';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { doc, updateDoc } from 'firebase/firestore';

const DueDiligence = ({ formData, onInputChange, companyId }) => {
  const [uploading, setUploading] = useState(null);
  const [error, setError] = useState(null);

  const handleFileUpload = async (documentIndex, e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file type
    if (!file.type.includes('pdf')) {
      setError('Please upload a PDF file');
      return;
    }

    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('File size must be less than 10MB');
      return;
    }

    setUploading(documentIndex);
    setError(null);

    try {
      // Upload to Firebase Storage
      const storageRef = ref(storage, `users/demoUser/documents/${file.name}`);
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);

      // Update Firestore document
      if (companyId) {
        const companyDoc = doc(db, 'companies', companyId);
        const updatedDocuments = [...formData.documents];
        updatedDocuments[documentIndex] = {
          ...updatedDocuments[documentIndex],
          uploaded: true,
          fileName: file.name,
          url: downloadURL,
          lastUpdated: new Date().toISOString()
        };

        await updateDoc(companyDoc, {
          documents: updatedDocuments
        });
      }

      // Update local state
      const updatedDocuments = [...formData.documents];
      updatedDocuments[documentIndex] = {
        ...updatedDocuments[documentIndex],
        uploaded: true,
        fileName: file.name,
        url: downloadURL,
        lastUpdated: new Date().toISOString()
      };

      onInputChange('documents', 'documents', updatedDocuments);
    } catch (error) {
      console.error('Error uploading document:', error);
      setError('Failed to upload file. Please try again.');
    } finally {
      setUploading(null);
    }
  };

  const handleRemoveDocument = async (documentIndex) => {
    const doc = formData.documents[documentIndex];
    
    try {
      // Delete from Firebase Storage if URL exists
      if (doc.url) {
        const storageRef = ref(storage, `users/demoUser/documents/${doc.fileName}`);
        await deleteObject(storageRef);
      }

      // Update Firestore document
      if (companyId) {
        const companyDoc = doc(db, 'companies', companyId);
        const updatedDocuments = [...formData.documents];
        updatedDocuments[documentIndex] = {
          ...updatedDocuments[documentIndex],
          uploaded: false,
          fileName: '',
          url: '',
          lastUpdated: null
        };

        await updateDoc(companyDoc, {
          documents: updatedDocuments
        });
      }

      // Update local state
      const updatedDocuments = [...formData.documents];
      updatedDocuments[documentIndex] = {
        ...updatedDocuments[documentIndex],
        uploaded: false,
        fileName: '',
        url: '',
        lastUpdated: null
      };

      onInputChange('documents', 'documents', updatedDocuments);
    } catch (error) {
      console.error('Error removing document:', error);
      setError('Failed to remove document. Please try again.');
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-6">
        {formData.documents.map((doc, index) => (
          <div
            key={index}
            className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white rounded-xl shadow-sm">
                  <FileText className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-900">{doc.name}</h3>
                  {doc.required && (
                    <span className="text-xs text-red-600">Required</span>
                  )}
                </div>
              </div>
              {doc.uploaded && (
                <button
                  type="button"
                  onClick={() => handleRemoveDocument(index)}
                  className="text-sm text-red-600 hover:text-red-700"
                >
                  Remove
                </button>
              )}
            </div>

            {!doc.uploaded ? (
              <div>
                <label className="inline-flex items-center px-4 py-2 border border-transparent rounded-xl shadow-sm text-sm font-medium text-white bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 cursor-pointer">
                  <input
                    type="file"
                    className="hidden"
                    accept=".pdf"
                    onChange={(e) => handleFileUpload(index, e)}
                    disabled={uploading === index}
                  />
                  {uploading === index ? 'Uploading...' : 'Upload Document'}
                </label>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-sm text-green-600">
                <CheckCircle2 className="h-5 w-5" />
                <span>
                  {doc.fileName} • Last updated{' '}
                  {new Date(doc.lastUpdated).toLocaleDateString()}
                </span>
              </div>
            )}
          </div>
        ))}
      </div>

      {error && (
        <div className="p-4 bg-red-50 rounded-xl flex items-center gap-2">
          <AlertCircle className="h-5 w-5 text-red-500" />
          <span className="text-sm text-red-700">{error}</span>
          <button
            type="button"
            onClick={() => setError(null)}
            className="ml-auto text-red-500 hover:text-red-700"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      )}

      <div className="mt-8 p-4 bg-blue-50 rounded-xl">
        <h3 className="text-sm font-medium text-blue-800 mb-2">Tips for Due Diligence</h3>
        <ul className="text-sm text-blue-700 space-y-2">
          <li>• Ensure all required documents are uploaded</li>
          <li>• Keep documents up to date and current</li>
          <li>• Use clear, descriptive file names</li>
          <li>• Ensure all documents are properly formatted and legible</li>
          <li>• Include any relevant certifications or compliance documents</li>
        </ul>
      </div>
    </div>
  );
};

export default DueDiligence; 